import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

// Палитра цветов для radial-чартов (doughnut, pie, polarArea)
const PALETTE = [
  "#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#14B8A6",
  "#6366F1", "#D946EF", "#0EA5E9", "#A3E635", "#FB923C",
  "#E11D48", "#7C3AED", "#059669", "#DC2626", "#2563EB",
  "#CA8A04", "#9333EA", "#0D9488", "#C026D3", "#65A30D"
]

// Stimulus-контроллер для Chart.js графиков.
// Принимает данные как JSON через data-values, рендерит Chart.js canvas.
//
// Поддерживает: line, bar, doughnut, pie, radar, polarArea
// При изменении data-value автоматически перерисовывает график.
export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    type: { type: String, default: "bar" },
    data: { type: Object, default: {} },
    xLabel: { type: String, default: "" },
    yLabel: { type: String, default: "" },
    gridVisible: { type: Boolean, default: true },
    legendPosition: { type: String, default: "top" },
    showValues: { type: Boolean, default: false }
  }

  connect() {
    this.chart = null
    this.renderChart()
  }

  disconnect() {
    this.destroyChart()
  }

  dataValueChanged() {
    this.destroyChart()
    this.renderChart()
  }

  typeValueChanged() {
    this.destroyChart()
    this.renderChart()
  }

  renderChart() {
    if (!this.hasCanvasTarget) return

    const ctx = this.canvasTarget.getContext("2d")
    const data = this.dataValue || { labels: [], datasets: [] }

    // Normalize: ensure labels and datasets exist
    if (!data.labels) data.labels = []
    if (!data.datasets) data.datasets = []

    const isRadial = this.isRadialChart()

    // Для radial-чартов: каждый сегмент должен иметь свой цвет
    if (isRadial) {
      data.datasets = data.datasets.map(ds => {
        const count = data.labels.length
        if (!Array.isArray(ds.backgroundColor) || ds.backgroundColor.length < count) {
          ds.backgroundColor = this.generateColors(count)
          ds.borderColor = "#ffffff"
          ds.borderWidth = 2
        }
        return ds
      })
    }

    this.chart = new Chart(ctx, {
      type: this.typeValue,
      data: data,
      options: this.buildOptions(),
      plugins: this.buildPlugins()
    })
  }

  buildOptions() {
    const isRadial = this.isRadialChart()

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.legendPositionValue !== "none",
          position: this.legendPositionValue || "top"
        }
      }
    }

    if (!isRadial) {
      options.scales = {
        x: {
          title: {
            display: !!this.xLabelValue,
            text: this.xLabelValue
          },
          grid: {
            display: this.gridVisibleValue
          }
        },
        y: {
          title: {
            display: !!this.yLabelValue,
            text: this.yLabelValue
          },
          grid: {
            display: this.gridVisibleValue
          }
        }
      }
    }

    return options
  }

  // Inline-плагин для отображения значений над столбцами (без npm-зависимости)
  buildPlugins() {
    if (!this.showValuesValue || this.isRadialChart()) return []

    return [{
      id: "dae20DataLabels",
      afterDatasetsDraw(chart) {
        const { ctx } = chart
        ctx.save()

        chart.data.datasets.forEach((dataset, dsIndex) => {
          const meta = chart.getDatasetMeta(dsIndex)
          if (meta.hidden) return

          meta.data.forEach((element, index) => {
            const value = dataset.data[index]
            if (value === 0 || value === null || value === undefined) return

            const { x, y } = element.tooltipPosition()
            const text = typeof value === "number" && value % 1 !== 0
              ? value.toFixed(1)
              : String(value)

            ctx.fillStyle = "#374151"
            ctx.font = "bold 11px sans-serif"
            ctx.textAlign = "center"
            ctx.textBaseline = "bottom"
            ctx.fillText(text, x, y - 4)
          })
        })

        ctx.restore()
      }
    }]
  }

  // Генерирует массив цветов для radial-чарта
  generateColors(count) {
    const colors = []
    for (let i = 0; i < count; i++) {
      colors.push(PALETTE[i % PALETTE.length])
    }
    return colors
  }

  isRadialChart() {
    return ["doughnut", "pie", "radar", "polarArea"].includes(this.typeValue)
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }
}
